import React from 'react';

import type { MenuItem } from '../../services/api';

import styles from './CircularMenuText.module.css';

/**
 *
 */
type CircularMenuTextProps = {
  readonly menuItems: ReadonlyArray<MenuItem>;
  readonly radius: number;
  readonly activeMenuItem: string | null;
  readonly onMenuItemClick: (slug: string) => void;
};

/**
 * CircularMenuText Component
 *
 * Renders menu items as text that curves around the circular border
 * Uses SVG textPath for curved text, with two paths (clockwise and counter-clockwise)
 * to prevent text from appearing upside down
 * @param root0
 * @param root0.menuItems
 * @param root0.radius
 * @param root0.activeMenuItem
 * @param root0.onMenuItemClick
 */
const CircularMenuText: React.FC<CircularMenuTextProps> = ({
  menuItems,
  radius,
  activeMenuItem,
  onMenuItemClick,
}) => {
  const svgSize = radius * 2 + 100; // Add padding for text
  const centerX = svgSize / 2;
  const centerY = svgSize / 2;
  const totalItems = menuItems.length;
  const textOffset = 8; // Small offset for text to not touch circle

  // Path start offset in degrees - paths start before 12 o'clock
  // so text centered at the "offset" position appears at true 12 o'clock
  // Needs to be large enough to accommodate half of the longest menu label
  const pathStartOffset = 35; // degrees before top (allows text to extend backward)

  /**
   * Calculate the start point of the circular path at a given angle
   * @param angleDeg - angle in degrees (0 = top, clockwise)
   * @param r - radius
   */
  const getPointOnCircle = (angleDeg: number, r: number) => {
    const angleRad = (angleDeg * Math.PI) / 180;
    return {
      x: centerX + r * Math.sin(angleRad),
      y: centerY - r * Math.cos(angleRad),
    };
  };

  /**
   * Calculate positioning for each menu item
   * Item 0 starts at the top (12 o'clock) and continues clockwise
   * @param index
   */
  const calculateItemData = (index: number) => {
    // Each item occupies this fraction of the circle
    const fraction = index / totalItems;

    // Convert to angle in degrees (0° = top, clockwise)
    const angle = fraction * 360;

    // Items between 90° and 270° would appear upside down on a clockwise path
    const isBottomHalf = angle > 90 && angle < 270;

    let pathOffset: number;

    if (isBottomHalf) {
      // Use counter-clockwise path
      // The CCW path also starts offset before top, going the other direction
      // Map angle to path position, accounting for the path start offset
      pathOffset = (pathStartOffset + (360 - angle)) / 360;
    } else {
      // Use clockwise path
      // The path starts at -pathStartOffset degrees, so 0° maps to pathStartOffset/360
      pathOffset = (pathStartOffset + angle) / 360;
    }

    return { pathOffset, isBottomHalf };
  };

  return (
    <svg
      width={svgSize}
      height={svgSize}
      viewBox={`0 0 ${svgSize} ${svgSize}`}
      className={styles.circularMenuText}
    >
      <defs>
        {/* Clockwise circle path for top half items (0° to 90° and 270° to 360°) */}
        {/* Path starts at -pathStartOffset degrees (before 12 o'clock) so text at 0° can be centered */}
        {(() => {
          const cwRadius = radius + textOffset;
          const startPoint = getPointOnCircle(-pathStartOffset, cwRadius);
          const endPoint = getPointOnCircle(-pathStartOffset - 0.001, cwRadius); // Nearly full circle
          return (
            <path
              id="circlePathCW"
              d={`M ${startPoint.x}, ${startPoint.y} A ${cwRadius} ${cwRadius} 0 1 1 ${endPoint.x} ${endPoint.y}`}
              fill="none"
            />
          );
        })()}

        {/* Counter-clockwise circle path for bottom half items (90° to 270°) */}
        {/* Path starts at +pathStartOffset degrees (before 12 o'clock going CCW) */}
        {(() => {
          const ccwRadius = radius + textOffset * 2;
          const startPoint = getPointOnCircle(pathStartOffset, ccwRadius);
          const endPoint = getPointOnCircle(pathStartOffset + 0.001, ccwRadius); // Nearly full circle
          return (
            <path
              id="circlePathCCW"
              d={`M ${startPoint.x}, ${startPoint.y} A ${ccwRadius} ${ccwRadius} 0 1 0 ${endPoint.x} ${endPoint.y}`}
              fill="none"
            />
          );
        })()}

        <radialGradient id="glowGradient">
          <stop offset="0%" stopColor="rgba(255, 140, 100, 0.8)" />
          <stop offset="100%" stopColor="rgba(255, 105, 180, 0.8)" />
        </radialGradient>
      </defs>

      {/* Glowing circle border */}
      <circle
        cx={centerX}
        cy={centerY}
        r={radius}
        fill="none"
        stroke="url(#glowGradient)"
        strokeWidth="3"
        className={styles.circleBorder}
      />

      {/* Radial dividers between menu items */}
      {menuItems.map((_, index) => {
        // Calculate angle for divider (halfway between items)
        const angle = ((index + 0.5) / totalItems) * 360;
        const angleRad = (angle * Math.PI) / 180;

        // Start point (slightly inside circle)
        const innerRadius = radius - 5;
        const x1 = centerX + innerRadius * Math.sin(angleRad);
        const y1 = centerY - innerRadius * Math.cos(angleRad);

        // End point (slightly outside circle)
        const outerRadius = radius + 15;
        const x2 = centerX + outerRadius * Math.sin(angleRad);
        const y2 = centerY - outerRadius * Math.cos(angleRad);

        return (
          <line
            key={`divider-${index}`}
            x1={x1}
            y1={y1}
            x2={x2}
            y2={y2}
            stroke="rgba(255, 105, 180, 0.5)"
            strokeWidth="1.5"
          />
        );
      })}

      {/* Menu item text on curved path */}
      {menuItems.map((item, index) => {
        const { pathOffset, isBottomHalf } = calculateItemData(index);
        const isActive = item.slug === activeMenuItem;
        const pathId = isBottomHalf ? '#circlePathCCW' : '#circlePathCW';

        return (
          <text
            key={item._id}
            className={`${styles.menuText} ${isActive ? styles.active : ''}`}
            onClick={() => onMenuItemClick(item.slug)}
            style={{ cursor: 'pointer' }}
          >
            <textPath href={pathId} startOffset={`${pathOffset * 100}%`} textAnchor="middle">
              {item.label.toUpperCase()}
            </textPath>
          </text>
        );
      })}
    </svg>
  );
};

export default CircularMenuText;
